import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { AuthModal } from './AuthModal';
import { loginWithGoogle } from '../services/auth';
import { Button } from './ui/button';

export function DarkHolePage() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [authMode, setAuthMode] = useState<'login' | 'register' | 'reset'>('login');
  const [googleLoading, setGoogleLoading] = useState(false);

  useEffect(() => {
    if (currentUser) {
      navigate('/app', { replace: true });
    }
  }, [currentUser, navigate]);

  const handleGoogleSignIn = async () => {
    setGoogleLoading(true);
    const { error, redirect } = await loginWithGoogle();
    if (redirect) return;
    if (error) {
      console.error(error);
      setGoogleLoading(false);
    } else {
      navigate('/app', { replace: true });
    }
  };

  const openEmailLogin = () => {
    setAuthMode('login');
    setIsAuthModalOpen(true);
  };

  const openEmailRegister = () => {
    setAuthMode('register');
    setIsAuthModalOpen(true);
  };

  if (currentUser) return null;

  return (
    <div className="min-h-screen w-full bg-black flex flex-col items-center justify-center p-6">
      <div className="w-full max-w-sm space-y-6 text-center">
        <h1 className="text-2xl font-semibold tracking-tight text-white/90">
          Sign in
        </h1>
        <p className="text-sm text-white/50">
          Sign in with Google or email to continue.
        </p>

        <div className="space-y-3 pt-2">
          <Button
            type="button"
            variant="outline"
            className="w-full h-12 bg-transparent border-white/20 text-white hover:bg-white/10 hover:text-white hover:border-white/30"
            onClick={handleGoogleSignIn}
            disabled={googleLoading}
          >
            <svg className="mr-2 h-5 w-5" viewBox="0 0 24 24">
              <path
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                fill="#4285F4"
              />
              <path
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                fill="#34A853"
              />
              <path
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                fill="#FBBC05"
              />
              <path
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                fill="#EA4335"
              />
            </svg>
            {googleLoading ? 'Signing in…' : 'Continue with Google'}
          </Button>

          <Button
            type="button"
            variant="outline"
            className="w-full h-12 bg-transparent border-white/20 text-white hover:bg-white/10 hover:text-white hover:border-white/30"
            onClick={openEmailLogin}
          >
            Sign in with Email
          </Button>
        </div>

        <p className="text-xs text-white/40 pt-2">
          Don&apos;t have an account?{' '}
          <button
            type="button"
            className="underline hover:text-white/70 transition-colors"
            onClick={openEmailRegister}
          >
            Create account
          </button>
        </p>
      </div>

      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
        defaultMode={authMode}
      />
    </div>
  );
}
